import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

// Eagerly bundled German locale (default language, zero network-round-trip)
import de from './locales/de.json';

const isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV === true;

/**
 * Lade-URL der EN/PL-Locales. `version` ist ein Inhalts-Hash der
 * Locale-Dateien (vite.config.ts → VITE_LOCALES_VERSION) und ändert sich mit
 * jeder geänderten Übersetzung. So kann ein Browser keine ältere PL-Datei aus
 * dem Cache neben dem frisch gebündelten DE mischen (neue Schlüssel fielen
 * sonst auf Deutsch zurück). Ohne Version (Dev/Tests) bleibt die URL nackt.
 */
export function localesLoadPath(version?: string): string {
  const base = '/locales/{{lng}}/translation.json';
  return version ? `${base}?v=${encodeURIComponent(version)}` : base;
}

i18n
  // Lazy-load EN/PL from /public/locales/{lng}/translation.json
  .use(HttpBackend)
  // Detect language from localStorage key 'sh24.locale', then browser navigator
  .use(LanguageDetector)
  // Pass i18n instance to react-i18next
  .use(initReactI18next)
  .init({
    // ── Resources ────────────────────────────────────────────────────────────
    resources: {
      // DE is bundled — no HTTP request on initial load
      de: { translation: de },
    },
    // KRITISCH: Ohne partialBundledLanguages behandelt i18next die inline
    // `resources` als vollständigen Store und lädt EN/PL NICHT über das
    // Backend — changeLanguage('en') wechselt dann zwar die Sprache, hat aber
    // keine Ressourcen und fällt auf DE zurück ("Klick passiert nichts").
    partialBundledLanguages: true,
    // EN/PL are loaded lazily via HttpBackend when requested
    backend: {
      loadPath: localesLoadPath(import.meta.env?.VITE_LOCALES_VERSION as string | undefined),
    },

    // ── Language ──────────────────────────────────────────────────────────────
    supportedLngs: ['de', 'en', 'pl'],
    // Defensive: pinned to false to block Path-Traversal via crafted localStorage
    // values (e.g. '../../sensitive') — Security-Review i18n MEDIUM-02.
    nonExplicitSupportedLngs: false,
    fallbackLng: 'de',
    load: 'languageOnly', // strips region codes: 'de-DE' → 'de'
    defaultNS: 'translation',

    // ── Detection order ───────────────────────────────────────────────────────
    // Priority: localStorage['sh24.locale'] → fallbackLng ('de')
    //
    // 'navigator' is intentionally excluded: the Admin CRM is a German-first
    // tool. Without an explicit user preference in localStorage the UI must
    // render in German regardless of the browser's OS/Accept-Language.
    // Manual switches via LanguageSwitcher are persisted in localStorage under
    // 'sh24.locale' and therefore respected on the next load.
    detection: {
      order: ['localStorage'],
      lookupLocalStorage: 'sh24.locale',
      caches: ['localStorage'],
    },

    // ── Rendering ─────────────────────────────────────────────────────────────
    interpolation: {
      // React already escapes values — no double-escaping
      escapeValue: false,
    },
    returnEmptyString: false,

    // ── Dev helpers ───────────────────────────────────────────────────────────
    // In development: log missing keys to console so translators are notified.
    // In production: silently fall back (never POSTs anywhere — no backend endpoint).
    saveMissing: isDev,
    missingKeyHandler: isDev
      ? (lngs: readonly string[], ns: string, key: string) => {
          console.warn(`[i18n] Missing key "${key}" for language(s): ${lngs.join(', ')} (ns: ${ns})`);
        }
      : undefined,
  });

export default i18n;
